<template>
  <v-container>
    <v-card>
      <v-card-title>
        Workout Schedules
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openDialog()">Add New</v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredSchedules"
        item-key="seqNo"
        class="elevation-1"
        :items-per-page="itemsPerPage"
        :sort-by="['groupId']"
        :sort-desc="[false]"
        :group-by="['group.teamName', 'event.name']"
        show-group-by
      >
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" @click="openDialog(item)">edit</v-icon>
          <v-icon small @click="confirmDelete(item.seqNo)">delete</v-icon>
        </template>
        <template v-slot:item.swimmerCount="{ item }">
          {{ getSwimmerCount(item) }}
        </template>
        <template v-slot:item.availableSpots="{ item }">
          {{ getAvailableSpots(item) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog
      v-model="dialog"
      max-width="700px"
      scrollable
      @keydown.esc="dialog = false"
    >
      <v-card>
        <v-card-title class="headline font-weight-bold">
          {{ form.seqNo ? "Edit" : "Add New" }} Workout Schedule
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="6">
                <GroupSelect
                  v-model="form.groupId"
                  item-value="node.teamId"
                  item-text="node.teamName"
                  label="Select Group"
                  :multiple="false"
                  :rules="[(v) => !!v || 'Group is required']"
                />
              </v-col>
              <v-col cols="6">
                <EventCatalogSelect
                  v-model="form.eventId"
                  label="Select Event"
                  :multiple="false"
                  :rules="[(v) => !!v || 'Event is required']"
                />
              </v-col>
              <v-col cols="6">
                <FacilitySelect
                  v-model="form.facilityId"
                  item-value="node.facilityId"
                  item-text="node.name"
                  :multiple="false"
                  label="Choose Facility"
                  :rules="[(v) => !!v || 'Facility is required']"
                />
              </v-col>
              <v-col cols="6">
                <catValueDropDown
                  :outlined="true"
                  :catName="'WEEKDAYS'"
                  v-model.trim="form.dayOfWeek"
                  label="Day of Week"
                  :rules="[(v) => !!v || 'Day is required']"
                />
              </v-col>
              <v-col cols="6">
                <v-menu
                  ref="startTimeMenu"
                  v-model="startTimeMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.startTime"
                      label="Start Time"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[(v) => !!v || 'Start time is required']"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="form.startTime"
                    @change="startTimeMenu = false"
                    format="24hr"
                  ></v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  ref="endTimeMenu"
                  v-model="endTimeMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.endTime"
                      label="End Time"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[(v) => !!v || 'End time is required']"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="form.endTime"
                    @change="endTimeMenu = false"
                    format="24hr"
                  ></v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <catValueDropDown
                  :outlined="true"
                  :catName="'FACILITY'"
                  v-model.trim="form.workoutType"
                  label="Workout Type"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.maxAthletes"
                  label="Max Athletes"
                  type="number"
                  min="0"
                  :outlined="true"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.notes"
                  label="Notes"
                  rows="2"
                  auto-grow
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="sticky-action-bar">
          <v-spacer />
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveSchedule">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import catValueDropDown from "../../shared/cat_val_dropdown.vue";
import EventCatalogSelect from "../../shared/selectEventCatalog.vue";
import GroupSelect from "../../shared/selectGroup.vue";
import FacilitySelect from "../../shared/selectFacility.vue";

export default {
  components: {
    catValueDropDown,
    EventCatalogSelect,
    GroupSelect,
    FacilitySelect,
  },
  props: {
    eventId: [Number, Array],
    groupId: Number,
    facilityId: Number,
    competitiveFlag: [Number, Boolean], // Add this - can be 0/1 or true/false
    swimmerCount: Object,
  },
  data() {
    return {
      dialog: false,
      form: {},
      startTimeMenu: false,
      endTimeMenu: false,
      itemsPerPage: 50,
      headers: [
        { text: "Group", value: "group.teamName" },
        { text: "Event", value: "event.name" },
        { text: "Facility", value: "facility.name" },
        { text: "Day", value: "dayOfWeek" },
        { text: "Start Time", value: "startTime" },
        { text: "End Time", value: "endTime" },
        { text: "Active Swimmers", value: "swimmerCount" },
        { text: "Max Athletes", value: "maxAthletes" },
        { text: "Available Spots", value: "availableSpots" },
        { text: "Workout Type", value: "workoutType" },
        { text: "Notes", value: "notes" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters(["workoutSchedules", "clubPersonRel", "user"]),

    // Helper to normalize eventId to always be an array
    eventIds() {
      if (!this.eventId) return [];
      return Array.isArray(this.eventId) ? this.eventId : [this.eventId];
    },

    athleteData() {
      if (!this.clubPersonRel) {
        console.log("No clubPersonRel data");
        return [];
      }

      const filtered = this.clubPersonRel.filter((athlete) => {
        const matchesGroup = this.groupId
          ? athlete.teamId === this.groupId
          : true;

        // Handle array of eventIds
        const matchesEvent =
          this.eventIds.length > 0
            ? this.eventIds.includes(athlete.event.eventId)
            : true;

        const isActive = athlete.status !== "inactive";

        return matchesGroup && matchesEvent && isActive;
      });

      return filtered;
    },

    filteredSchedules() {
      return this.workoutSchedules.filter((s) => {
        // Handle array of eventIds
        const matchesEvent =
          this.eventIds.length > 0 ? this.eventIds.includes(s.eventId) : true;

        const matchesGroup = this.groupId ? s.groupId === this.groupId : true;
        const matchesFacility = this.facilityId
          ? s.facilityId === this.facilityId
          : true;
        return matchesEvent && matchesGroup && matchesFacility;
      });
    },
  },
  created() {
    this.allWorkoutSchedules();
  },
  methods: {
    ...mapActions([
      "allWorkoutSchedules",
      "workoutScheduleMerge",
      "workoutScheduleDelete",
    ]),

    getSwimmerCount(item) {
      console.log(
        `Getting swimmer count for group ${this.groupId}, competitive: ${this.competitiveFlag}`
      );

      // For competitive groups, show total active swimmers for the entire group
      if (this.competitiveFlag === 1 || this.competitiveFlag === true) {
        return this.getTotalActiveSwimmersForGroup();
      }

      // For non-competitive groups, show swimmers for this specific schedule/day
      return this.getSwimmersForSchedule(item.seqNo);
    },

    getTotalActiveSwimmersForGroup() {
      if (!this.athleteData) {
        console.log("No athleteData available");
        return 0;
      }

      const activeSwimmers = this.athleteData.filter((athlete) => {
        return athlete.teamId === this.groupId && athlete.status === "active";
      });

      console.log(
        `Competitive group ${this.groupId}: ${activeSwimmers.length} total active swimmers`
      );
      return activeSwimmers.length;
    },

    getSwimmersForSchedule(scheduleSeqNo) {
      console.log('Getting swimmers for specific schedule:', scheduleSeqNo);
      
      if (!this.athleteData || !scheduleSeqNo) {
        console.log('No athleteData or scheduleSeqNo');
        return 0;
      }

      const matchingAthletes = this.athleteData.filter((athlete) => {
        const participationDays = this.parseParticipationDays(athlete.participationDays);
        const matches = participationDays.includes(scheduleSeqNo);
        console.log(`Athlete ${athlete.personId} participationDays:`, participationDays, 'matches:', matches);
        return matches;
      });

      console.log(`Schedule ${scheduleSeqNo}: ${matchingAthletes.length} swimmers`);
      return matchingAthletes.length;
    },

    parseParticipationDays(participationDays) {
      if (!participationDays) return [];
      
      try {
        return typeof participationDays === 'string'
          ? JSON.parse(participationDays)
          : participationDays || [];
      } catch (error) {
        console.warn('Failed to parse participationDays:', participationDays, error);
        return [];
      }
    },

    getAvailableSpots(item) {
      const maxAthletes = item.maxAthletes;
      const currentSwimmers = this.getSwimmerCount(item);
      
      // If no max is set, show 0
      if (!maxAthletes || maxAthletes === 0) {
        return 0;
      }
      
      const available = maxAthletes - currentSwimmers;
      return available > 0 ? available : 0;
    },

    openDialog(item = {}) {
      this.form = {
        seqNo: null,
        groupId: this.groupId || null,
        facilityId: this.facilityId || null,
        eventId: this.eventIds.length === 1 ? this.eventIds[0] : null,
        dayOfWeek: "",
        startTime: "",
        endTime: "",
        workoutType: "",
        maxAthletes: null,
        notes: "",
        ...item,
      };
      this.dialog = true;
    },

    async saveSchedule() {
      try {
        this.form.updateSource = this.user.userName;
        await this.workoutScheduleMerge(this.form);
        this.dialog = false;
        this.$emit("snackbar", "Workout schedule saved successfully!");
      } catch (error) {
        console.error("Failed to save workout schedule:", error);
        this.$emit(
          "snackbar",
          "Failed to save workout schedule. Please try again."
        );
      }
    },

    confirmDelete(seqNo) {
      if (confirm("Are you sure you want to delete this workout schedule?")) {
        this.workoutScheduleDelete({ seqNo }).then(() => {
          this.$emit("snackbar", "Workout schedule deleted.");
        });
      }
    },
  },
};
</script>

<style scoped>
.sticky-action-bar {
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid #ccc;
  z-index: 2;
}
</style>
